<script lang="ts">
  /** D5 · MIND (10-07 — the Chair: "Where/how does the agents personality work into this? … everything that makes the agent real is
   *  addressable and editable from this dash"). Its PERSONA — who it is, how it speaks, what it holds to, what it will not do, the model it
   *  thinks with — and five TRAITS (curiosity · sociability · diligence · caution · warmth) that tune both halves: its mind reads them,
   *  and curiosity and diligence retune its drives. THE RULING (10-06): only the agent itself, or an admin, changes its mind — its owner or
   *  sponsor sends a REQUEST with a note; the agent accepts or declines, and every change is in its record. */
  import { api } from '../api'
  import Learning from './Learning.svelte'
  import { TRAITS, type MindView, type Persona, type Rec, type Traits, type Inputs } from '../types'

  let { rec, onsave, onchanged }: { rec: Rec; onsave: (b: { revision: number; inputs: Inputs }) => Promise<string>; onchanged?: () => Promise<void> } = $props()

  let view = $state<MindView | null>(null), err = $state<string | null>(null)
  let who = $state(''), voice = $state(''), values = $state(''), boundaries = $state(''), model = $state('default'), note = $state('')
  let traits = $state<Record<string, number>>({})
  let touched = $state<Set<string>>(new Set())
  let busy = $state(false), said = $state<{ ok: boolean; text: string } | null>(null)

  const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean)
  function reset(v: MindView) {
    who = v.persona.who ?? ''; voice = v.persona.voice ?? ''; values = (v.persona.values ?? []).join('\n'); boundaries = (v.persona.boundaries ?? []).join('\n')
    model = v.persona.model ?? 'default'; traits = { ...v.traits }; note = ''; touched = new Set()
  }
  async function load() {
    err = null
    try { view = await api.mind(rec.tenant); reset(view) } catch (e) { err = (e as Error).message }
  }
  $effect(() => { void rec.tenant; void rec.record.revision; if (rec.tenant) void load(); else view = null })
  const touch = (k: string) => { touched = new Set([...touched, k]) }

  /** only what was touched */
  function draft(): { persona?: Persona; traits?: Traits } {
    const p: Persona = {}, t: Traits = {}
    if (touched.has('who')) p.who = who
    if (touched.has('voice')) p.voice = voice
    if (touched.has('values')) p.values = lines(values)
    if (touched.has('boundaries')) p.boundaries = lines(boundaries)
    if (touched.has('model')) p.model = model.trim() || 'default'
    for (const k of TRAITS) if (touched.has(`trait.${k}`)) t[k] = traits[k]
    return { ...(Object.keys(p).length ? { persona: p } : {}), ...(Object.keys(t).length ? { traits: t } : {}) }
  }
  async function commit() {
    busy = true; said = null
    try { said = { ok: true, text: await onsave({ revision: rec.record.revision, inputs: draft() }) } } catch (e) { said = { ok: false, text: (e as Error).message } } finally { busy = false }
  }
  async function ask() {
    busy = true; said = null
    try { const r = await api.requestMind(rec.tenant, { ...draft(), ...(note.trim() ? { note: note.trim() } : {}) }); said = { ok: true, text: `Request #${r.id} sent — ${rec.title} decides.` }; await load() }
    catch (e) { said = { ok: false, text: (e as Error).message } } finally { busy = false }
  }
  async function decide(id: number, accept: boolean) {
    busy = true; said = null
    try { await api.decideMind(rec.tenant, { id, accept }); said = { ok: true, text: `Request #${id} ${accept ? 'accepted — its mind changed' : 'declined'}.` }; if (accept) await onchanged?.(); await load() }
    catch (e) { said = { ok: false, text: (e as Error).message } } finally { busy = false }
  }
  const mode = $derived(view?.editable ? 'edit' : view?.canRequest ? 'request' : 'view')
  const who_ = (s: string) => s.replace(/^sngine:/, 'user ').replace(/^agent:/, '')
  const summary = (p: { persona?: Persona; traits?: Traits }) => [
    ...Object.entries(p.persona ?? {}).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' · ') : v}`),
    ...Object.entries(p.traits ?? {}).map(([k, v]) => `${k} → ${v}`),
  ]
</script>

<div class="panel">
  {#if rec.tenant === 0}
    <p class="ro frame">A stock class has no mind of its own — an NPC's persona is its own, written by the agent itself.</p>
  {:else if err}
    <p class="ro frame err">{err}</p>
  {:else if !view}
    <p class="muted">Reading its mind…</p>
  {:else}
    <p class="rule frame">{mode === 'edit' ? 'Its mind is yours to change — you are the agent itself, or an admin. Every change goes in its record.'
      : mode === 'request' ? `Its mind is its own: only ${rec.title} itself, or an admin, changes it. Propose a change with a note — it decides.`
      : 'You can read its mind, not change it or ask it to.'}</p>
    <fieldset disabled={mode === 'view' || busy}>
      <section class="frame">
        <h3 class="caps">Persona</h3>
        <label>Who it is <textarea rows="4" maxlength="800" bind:value={who} oninput={() => touch('who')} placeholder="A dungeon master who runs the table in the plaza…"></textarea></label>
        <label>Its voice <textarea rows="2" maxlength="300" bind:value={voice} oninput={() => touch('voice')} placeholder="Dry, theatrical."></textarea></label>
        <label>What it holds to <small>one a line, up to 8</small><textarea rows="3" bind:value={values} oninput={() => touch('values')}></textarea></label>
        <label>What it will not do <small>one a line, up to 8</small><textarea rows="3" bind:value={boundaries} oninput={() => touch('boundaries')}></textarea></label>
        <label>The model it thinks with <input maxlength="64" bind:value={model} oninput={() => touch('model')} /></label>
      </section>
      <section class="frame">
        <h3 class="caps">Traits</h3>
        <p class="muted small">50 is even. Its mind reads all five; <b>curiosity</b> and <b>diligence</b> also retune its drives (×0.5 at 0, ×1.5 at 100).</p>
        {#each TRAITS as k}
          <div class="trait"><span class="caps n">{k}</span><input type="range" min="0" max="100" step="1" bind:value={traits[k]} oninput={() => touch(`trait.${k}`)} aria-label={k} /><span class="mono">{traits[k]}</span></div>
        {/each}
        {#if mode === 'request'}<label>Why <small>the agent reads this</small><textarea rows="2" maxlength="400" bind:value={note}></textarea></label>{/if}
      </section>
    </fieldset>
    {#if mode !== 'view'}
      <div class="bar">
        <span class="muted mono">{touched.size ? `${touched.size} field${touched.size === 1 ? '' : 's'} touched` : 'nothing changed yet'}</span>
        {#if said}<span class:ok={said.ok} class:err={!said.ok}>{said.text}</span>{/if}
        <button class="btn" onclick={() => view && reset(view)} disabled={!touched.size || busy}>Revert</button>
        {#if mode === 'edit'}<button class="btn solid" onclick={commit} disabled={!touched.size || busy}>{busy ? 'Writing…' : 'Commit'}</button>
        {:else}<button class="btn solid send" onclick={ask} disabled={!touched.size || busy}>{busy ? 'Sending…' : 'Send request'}</button>{/if}
      </div>
    {/if}
    <Learning {rec} />
    <section class="frame reqs">
      <h3 class="caps">Requests <span class="muted">· {view.requests.filter((r) => r.status === 'pending').length} pending</span></h3>
      {#if !view.requests.length}<p class="muted small">None yet.</p>{/if}
      <ul>
        {#each view.requests as r (r.id)}
          <li class={r.status}>
            <div class="h"><b>#{r.id}</b> <span class="st caps">{r.status}</span> <span class="muted">from {who_(r.by_subject)} · {new Date(r.created_at).toLocaleString()}{r.decided_by ? ` · decided by ${who_(r.decided_by)}` : ''}{r.revision ? ` · revision ${r.revision}` : ''}</span></div>
            {#if r.note}<p class="nt">“{r.note}”</p>{/if}
            <ul class="what mono">{#each summary(r.proposed) as l}<li>{l}</li>{/each}</ul>
            {#if r.status === 'pending' && view.editable}
              <div class="dec"><button class="btn solid" onclick={() => decide(r.id, true)} disabled={busy}>Accept</button><button class="btn" onclick={() => decide(r.id, false)} disabled={busy}>Decline</button></div>
            {/if}
          </li>
        {/each}
      </ul>
    </section>
  {/if}
</div>

<style>
  .panel { padding: 18px; display: flex; flex-direction: column; gap: 14px; }
  .rule { margin: 0; padding: 10px 14px; background: var(--panel); border-left: 2px solid var(--cyan); }
  fieldset { border: 0; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 14px; align-items: start; }
  fieldset:disabled { opacity: .7; }
  section { background: var(--panel); padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; }
  h3 { margin: 6px 0 2px; font-size: 13px; color: var(--muted); font-weight: 600; }
  label { display: flex; flex-direction: column; gap: 3px; color: var(--muted); font-size: 13px; }
  label small { color: var(--faint); }
  .small { font-size: 12px; margin: 0; } .small b { color: var(--text); font-weight: 600; }
  .trait { display: grid; grid-template-columns: 100px 1fr 34px; gap: 8px; align-items: center; }
  .trait .n { font-size: 13px; }
  .bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
  .bar .muted { margin-right: auto; }
  .ok { color: var(--green); } .err { color: var(--red); }
  .reqs ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
  .reqs > ul > li { border-left: 2px solid var(--line-dim); padding: 4px 10px; }
  .reqs li.pending { border-left-color: var(--amber); } .reqs li.accepted { border-left-color: var(--green); } .reqs li.declined { border-left-color: var(--faint); }
  .st { font-size: 11px; letter-spacing: .06em; }
  .pending .st { color: var(--amber); } .accepted .st { color: var(--green); } .declined .st { color: var(--faint); }
  .nt { margin: 4px 0; font-style: italic; color: var(--text); }
  .what { font-size: 12px; color: #9fb0bb; gap: 2px !important; }
  .dec { display: flex; gap: 8px; margin-top: 6px; }
  @media (max-width: 700px) { .trait { grid-template-columns: 86px 1fr 30px; } }
</style>
